/* eslint-disable @typescript-eslint/no-explicit-any */
import { isPlatformBrowser } from "@angular/common";
import { inject, Injectable, PLATFORM_ID } from "@angular/core";
import { environment } from "../../environments/environment";

/**
 * Serviço de autenticação Apple (Sign In with Apple) para a Web.
 *
 * Diferente do app mobile (que usa a capability nativa do iOS via
 * expo-apple-authentication), na Web o mecanismo é o Apple JS SDK
 * (`AppleID.auth`). O fluxo é:
 *   1. signIn() abre o popup da Apple e devolve `authorization.id_token` (JWT)
 *      + `user.name` (preenchido SÓ no primeiro consentimento).
 *   2. O componente envia { provider: "apple", token: id_token, name, last_name }
 *      para o backend (POST /auth/social_sign_in), que valida o JWT contra as
 *      chaves públicas da Apple.
 *
 * Importante: a Apple só devolve o NOME no PRIMEIRO authorization. Repassamos
 * para o backend usar somente na criação do usuário (depois ele ignora).
 *
 * O `clientId` (Services ID) precisa ser o MESMO configurado como
 * APPLE_SERVICES_ID no backend — é o `aud` esperado dentro do id_token.
 */

declare const AppleID: any;

export interface AppleSignInResult {
  /** identityToken (JWT) — vai como `token` para o backend */
  identityToken: string;
  /** Nome — preenchido só no primeiro login; indefinido nas vezes seguintes */
  name?: string;
  lastName?: string;
}

const APPLE_SDK_URL =
  "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";
const APPLE_SDK_SCRIPT_ID = "apple-auth-sdk";

@Injectable({ providedIn: "root" })
export class AppleAuthService {
  private readonly platformId = inject(PLATFORM_ID);
  private sdkPromise: Promise<void> | null = null;
  private initialized = false;

  /** Sign In with Apple na Web exige browser + Services ID configurado. */
  isAvailable(): boolean {
    return isPlatformBrowser(this.platformId) && !!environment.APPLE_CLIENT_ID;
  }

  async signIn(): Promise<AppleSignInResult> {
    if (!isPlatformBrowser(this.platformId)) {
      throw new Error("Login com Apple disponível apenas no navegador.");
    }
    if (!environment.APPLE_CLIENT_ID) {
      throw new Error("Login com Apple indisponível no momento.");
    }

    await this.ensureSdkLoaded();
    this.ensureInitialized();

    try {
      const data = await AppleID.auth.signIn();
      const identityToken: string | undefined = data?.authorization?.id_token;

      if (!identityToken) {
        throw new Error("Login com Apple não concluído. Tente novamente.");
      }

      return {
        identityToken,
        name: data?.user?.name?.firstName ?? undefined,
        lastName: data?.user?.name?.lastName ?? undefined,
      };
    } catch (error: any) {
      // Usuário fechou/cancelou o popup da Apple
      if (
        error?.error === "popup_closed_by_user" ||
        error?.error === "user_cancelled_authorize" ||
        error?.error === "user_trigger_new_signin_flow"
      ) {
        throw new Error("Login com Apple cancelado. Tente novamente.");
      }
      if (error instanceof Error) {
        throw error;
      }
      throw new Error("Não foi possível entrar com a Apple. Tente novamente.");
    }
  }

  private ensureInitialized(): void {
    if (this.initialized) {
      return;
    }
    AppleID.auth.init({
      clientId: environment.APPLE_CLIENT_ID,
      scope: "name email",
      redirectURI: environment.APPLE_REDIRECT_URI,
      usePopup: true,
    });
    this.initialized = true;
  }

  private ensureSdkLoaded(): Promise<void> {
    if (typeof AppleID !== "undefined") {
      return Promise.resolve();
    }
    if (this.sdkPromise) {
      return this.sdkPromise;
    }

    this.sdkPromise = new Promise<void>((resolve, reject) => {
      const existing = document.getElementById(APPLE_SDK_SCRIPT_ID);
      if (existing) {
        existing.addEventListener("load", () => resolve());
        existing.addEventListener("error", () =>
          reject(new Error("Falha ao carregar o SDK da Apple.")),
        );
        return;
      }

      const script = document.createElement("script");
      script.id = APPLE_SDK_SCRIPT_ID;
      script.src = APPLE_SDK_URL;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () =>
        reject(new Error("Falha ao carregar o SDK da Apple."));
      document.head.appendChild(script);
    });

    return this.sdkPromise;
  }
}
